import { useCallback, useEffect, useRef, useState } from "react";

export type MessageType = "success" | "error" | "info" | "warning";

export interface Message {
    type: MessageType;
    content: string;
    id: number;
}

export function useMessage(autoHideDelay: number = 3000) {
    const [message, setMessageState] = useState<Message | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const messageIdRef = useRef(0);

    const setMessage = useCallback(
        (type: MessageType, content: string) => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            const newMessage: Message = {
                type,
                content,
                id: ++messageIdRef.current,
            };

            setMessageState(newMessage);

            // Set auto-hide timer
            timeoutRef.current = setTimeout(() => {
                setMessageState(current =>
                    current?.id === newMessage.id ? null : current,
                );
                timeoutRef.current = null;
            }, autoHideDelay);
        },
        [autoHideDelay],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return {
        message,
        setMessage,
    };
}
